import React, { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2, User, AlertTriangle, Pill, Calendar, Activity } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const BAND_STYLE = {
    critical: { bg: "bg-[#F9ECEC]", text: "text-[#9E2A2B]", label: "Crítico" },
    high:     { bg: "bg-[#FBEEE6]", text: "text-[#C85A47]", label: "Alto" },
    moderate: { bg: "bg-[#FBF4E5]", text: "text-[#8A6A15]", label: "Moderado" },
    low:      { bg: "bg-[#EDF3E8]", text: "text-[#556B2F]", label: "Baixo" },
};

/**
 * Resumo clínico híbrido no topo do prontuário:
 *  - narrativa curta (3-4 linhas) gerada por Claude Sonnet 4.5
 *  - chips estruturados: idade, sexo, problemas P0, meds ativas, última consulta, risco
 *  - botão "Atualizar resumo" (regenera com IA + persiste em cache)
 *
 * Endpoints:
 *   GET  /api/patients/{id}/clinical-summary
 *   POST /api/patients/{id}/clinical-summary/refresh
 */
export default function ClinicalSummaryCard({ patientId, canGenerate }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/patients/${patientId}/clinical-summary`);
                setData(data);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [patientId]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post(`/patients/${patientId}/clinical-summary/refresh`, {});
            setData(data);
            toast.success("Resumo atualizado com IA.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRefreshing(false); }
    };

    if (loading) {
        return (
            <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical" data-testid="clinical-summary-loading">
                <Loader2 size={14} className="animate-spin" /> Carregando resumo clínico…
            </div>
        );
    }
    if (!data) return null;

    const chips = data.chips || {};
    const risk = BAND_STYLE[chips.risk_band] || BAND_STYLE.low;

    return (
        <div className="card-earthy border-l-4 border-[#2E7D6B] bg-gradient-to-br from-[#FBF7EF] to-white" data-testid="clinical-summary">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#2E7D6B] text-white flex items-center justify-center shrink-0">
                        <Sparkles size={16} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-[#1F5A4C] font-semibold">Resumo clínico</p>
                        <h3 className="font-display text-base text-brand font-medium truncate">
                            {data.patient?.full_name}
                        </h3>
                    </div>
                </div>
                {canGenerate && (
                    <button
                        onClick={refresh}
                        disabled={refreshing}
                        data-testid="clinical-summary-refresh"
                        className="btn-secondary text-xs"
                    >
                        {refreshing
                            ? <><Loader2 size={12} className="animate-spin" /> Gerando…</>
                            : <><RefreshCw size={12} /> {data.narrative ? "Atualizar" : "Gerar resumo"}</>}
                    </button>
                )}
            </div>

            {/* Narrativa */}
            <div className="mb-3">
                {data.narrative ? (
                    <p className="text-sm text-brand leading-relaxed" data-testid="clinical-summary-narrative">
                        {data.narrative}
                    </p>
                ) : (
                    <p className="text-sm text-muted-clinical italic bg-alt p-3 rounded-lg" data-testid="clinical-summary-empty">
                        Sem resumo IA gerado ainda. Clique em <b>Gerar resumo</b> para criar uma narrativa clínica dos dados atuais.
                    </p>
                )}
                {data.generated_at && (
                    <p className="text-[10px] text-muted-clinical mt-1.5">
                        Gerado por IA · {new Date(data.generated_at).toLocaleString("pt-BR")}
                        {data.stale && <span className="ml-2 text-[#C85A47] font-semibold">· desatualizado (recomenda-se regenerar)</span>}
                    </p>
                )}
            </div>

            {/* Chips estruturados */}
            <div className="flex flex-wrap gap-2" data-testid="clinical-summary-chips">
                {chips.age != null && (
                    <Chip icon={User} label={`${chips.age}a`} sub={chips.sex === "M" ? "M" : chips.sex === "F" ? "F" : ""} />
                )}
                {chips.problems_p0 > 0 && (
                    <Chip icon={AlertTriangle} label={`${chips.problems_p0} P0`} sub="problemas" tone="critical" />
                )}
                {chips.problems_total > 0 && (
                    <Chip icon={AlertTriangle} label={`${chips.problems_total}`} sub="problemas ativos" />
                )}
                {chips.active_med_count > 0 && (
                    <Chip icon={Pill} label={`${chips.active_med_count}`} sub="medicações" tone={chips.active_med_count >= 5 ? "warning" : "neutral"} />
                )}
                {chips.critical_calculators > 0 && (
                    <Chip icon={Activity} label={`${chips.critical_calculators}`} sub="escalas críticas" tone="critical" />
                )}
                {chips.last_consult_at && (
                    <Chip icon={Calendar} label={new Date(chips.last_consult_at).toLocaleDateString("pt-BR")} sub="última consulta" />
                )}
                {chips.risk_band && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${risk.bg} ${risk.text}`}>
                        Risco: {risk.label} ({chips.risk_score})
                    </span>
                )}
            </div>
        </div>
    );
}

function Chip({ icon: Icon, label, sub, tone = "neutral" }) {
    const toneClass = {
        neutral:  "bg-alt text-brand",
        critical: "bg-[#F9ECEC] text-[#9E2A2B]",
        warning:  "bg-[#FBF4E5] text-[#8A6A15]",
    }[tone] || "bg-alt text-brand";
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${toneClass}`}>
            <Icon size={11} />
            <b>{label}</b>
            {sub && <span className="opacity-70 text-[10px] uppercase tracking-widest">{sub}</span>}
        </span>
    );
}
